import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Cardul de tablou pentru ecranele care isi DESENEAZA singure trupul.
 *
 * -----------------------------------------------------------------------------
 * DE CE MAI E NEVOIE DE UNUL, CAND EXISTA `DashboardCardSlot`
 * -----------------------------------------------------------------------------
 *
 * `DashboardCardSlot` pune in loc un sir de HTML venit din API (`CardMarkup`,
 * adica `card.php`). Asa merg cele cinci tablouri de carduri - Platform si cele
 * de modul: cardul e o CITIRE din catalog, si serverul stie sa o scrie.
 *
 * Tablourile de MASURATORI - Endpoint Performance, SNMP, Core Applications -
 * n-au citiri in catalog: au grafice desenate de mana in SVG, in React, din
 * raspunsul propriu al ecranului. Un sir de HTML nu le poate purta (SVG-ul are
 * balon la trecerea mausului si se re-deseneaza cand se schimba latimea), deci
 * cardul trebuie sa primeasca COPII, nu text.
 *
 * Restul e identic si TREBUIE sa ramana identic: aceeasi foaie
 * (`od_dashboard.css`), aceeasi grila (`.odb`), acelasi cap de card
 * (`.odb-head` cu placuta modulului, numele modulului si titlul), aceeasi
 * cifra mare (`.odb-figure`). Un ecran care si-ar desena propriul cartonas
 * "asemanator" ar fi al saselea dialect pe acelasi portal.
 *
 * -----------------------------------------------------------------------------
 * `.odb-card` E COPILUL DIRECT AL LUI `.odb-slot`
 * -----------------------------------------------------------------------------
 *
 * Regula din foaie e scrisa pe copilul direct:
 *
 *     .odb-slot > .odb-card { min-height: 0; overflow: hidden; }
 *
 * Un singur `<div>` pus intre ele o face sa nu se mai potriveasca, si atunci
 * cardul nu se mai taie la marginea celulei: creste peste vecinul de sub el.
 * Asa s-au asezat cardurile unele peste altele pe cele cinci tablouri, si de
 * asta s-a scris si la `DashboardCardSlot`. Aici nu exista niciun invelis, si
 * nu are voie sa apara unul.
 */

/** Numele modulelor, ca in `Catalog::$modules` - placuta poarta eticheta lui. */
export const ODB_MODULES: Record<string, string> = {
  overview: 'Platform',
  inventoree: 'Inventoree',
  softsee: 'Softsee',
  opteemum: 'Opteemum',
  proactivitee: 'Proactivitee',
  /* Productivitee NU e in `Catalog::$modules` - fisierul ala e comun cu
     portalul care ruleaza la banca - dar tabloul lui isi deseneaza singur
     cardurile, si placuta lor are nevoie de numele modulului. */
  productivitee: 'Productivitee',
  easeedesk: 'Easeedesk',
  securitee: 'Securitee',
  printeeng: 'Printeeng',
  protectee: 'Protectee',
}

/**
 * Inaltimea ramasa pentru desen, MASURATA.
 *
 * Grila are randuri de inaltime FIXA (`grid-auto-rows: var(--odb-cell)`), iar
 * celula creste cu ecranul: `max(200px, (100vh - 240 - 2*gap) / 3)`. Un grafic
 * cu inaltimea scrisa de mana ar fi iesit ori taiat pe un laptop, ori cu o
 * palma de alb sub el pe un monitor mare. Se masoara locul si se deseneaza cat
 * incape.
 *
 * Cand masurarea da zero - prima trecere, sau un jsdom care nu asaza nimic - se
 * intoarce inaltimea de rezerva: un grafic care asteapta o masuratoare ca sa
 * apara e, pentru cine se uita la ecran, un grafic lipsa. Aceeasi socoteala ca
 * `useWidth` din `EndpointCharts.tsx`.
 */
export function useBoxHeight(fallback = 260) {
  const box = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setHeight(el.clientHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [box, height > 0 ? height : fallback] as const
}

/**
 * Pagina unui tablou: fundalul si marginile zonei, apoi bara si grila.
 *
 * `.odb-page` isi anuleaza singura cei 20px ai lui `#page-content` si pune 16
 * ai ei - vezi capul foii. De-aia ecranul NU se mai infasoara in `<Content>`:
 * acolo ar fi intrat intr-un `#content` cu randuri Bootstrap, adica tocmai
 * asezarea de care zona asta se desparte.
 */
export function OdbBoard({ bar, children }: { bar?: ReactNode; children: ReactNode }) {
  return (
    <div className="odb-page">
      {bar}
      <div className="odb" id="odbGrid">
        {children}
      </div>
    </div>
  )
}

export function OdbCard({
  module,
  icon,
  title,
  value,
  unit,
  tone,
  w = 1,
  h = 1,
  id,
  children,
}: {
  /** Cheia din `ODB_MODULES` - da placutei culoarea si numele modulului. */
  module: string
  /** Iconita Font Awesome de pe placuta, fara prefixul `fas`. */
  icon: string
  title: string
  /** Cifra mare din cap, daca ecranul are una de aratat pentru cardul asta. */
  value?: ReactNode
  unit?: string
  /** `ok` / `warn` / `crit` - coloreaza cifra, ca la cardurile din catalog. */
  tone?: 'ok' | 'warn' | 'crit'
  /** Cate coloane si cate randuri tine cardul: 1 sau 2, ca in `Card::$sizes`. */
  w?: 1 | 2
  h?: 1 | 2
  id?: string
  /**
   * Trupul cardului. Ca functie, primeste inaltimea masurata a locului ramas,
   * ca un grafic sa se poata desena exact pe ea.
   */
  children?: ReactNode | ((height: number) => ReactNode)
}) {
  const [box, height] = useBoxHeight()

  return (
    <div className={`odb-slot odb-x${w} odb-y${h}`} data-card={id ?? title}>
      <div className={`odb-card odb-m-${module}${tone ? ` odb-t-${tone}` : ''}`}>
        <div className="odb-head">
          {value !== undefined && (
            <span className="odb-figure">
              <span className="odb-value">
                {value}
                {unit ? <span className="odb-unit">{unit}</span> : null}
              </span>
            </span>
          )}
          <span className="odb-tile">
            <i className={`fas ${icon}`} aria-hidden="true"></i>
          </span>
          <span className="odb-names">
            <span className="odb-mod">{ODB_MODULES[module] ?? module}</span>
            <span className="odb-title">{title}</span>
          </span>
        </div>
        {children !== undefined && (
          <div className="odb-body" ref={box} id={id}>
            {typeof children === 'function' ? children(height) : children}
          </div>
        )}
      </div>
    </div>
  )
}
